import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Unlock } from 'lucide-react';
interface SlideCentralizeToLocalizeProps {
  onContinue: () => void;
}
export function SlideCentralizeToLocalize({
  onContinue
}: SlideCentralizeToLocalizeProps) {
  const [stage, setStage] = useState(0);
  const centralize = ['Campaign strategy', 'Scientific narrative', 'Core claims', 'Brand standards', 'Reusable assets', 'Production workflows', 'Compliance governance', 'Measurement standards', 'Performance learning'];
  const decentralize = ['Campaign activation', 'Local market insight', 'Language', 'Cultural context', 'Channel selection', 'Market-specific emphasis', 'Local calls to action', 'Local compliance governance'];
  useEffect(() => {
    const timers = [300, 900, 1600, 2400].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-283" data-mp="38b6u2x57gixj">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3" data-id="element-284" data-mp="3dab1pjche8lr">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-285" data-mp="3m3wif887hzxt">
          The Principle
        </p>
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-286" data-mp="2peyilbllnbts">
          You have to centralize to localize
        </h2>
        <p className="text-lg text-white/60" data-id="element-287" data-mp="2uekzbv0mza7c">
          Centralize everything that should not be rebuilt in every market.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-6" data-id="element-288" data-mp="33e7yea5w83x8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-289" data-mp="dmt8ihhyw4gg">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="bg-teal-400/10 border border-teal-400/30 rounded-2xl p-6" data-id="element-290" data-mp="imxq6aum3589">
            <div className="flex items-center gap-3 mb-5" data-id="element-291" data-mp="2oorxjdq24vvw">
              <div className="w-10 h-10 rounded-xl bg-teal-400/20 text-teal-400 flex items-center justify-center" data-id="element-292" data-mp="2olw2py9ianbo">
                <Lock className="w-5 h-5" data-id="element-293" data-mp="1vzkt5y3x9c2q" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-294" data-mp="1vzuj5loyvj8f">Centralize</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-id="element-295" data-mp="1r22t6x7vu76v">
              {centralize.map((item, idx) => <motion.div key={item} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              y: stage >= 2 ? 0 : 8
            }} transition={{
              delay: stage >= 2 ? idx * 0.06 : 0
            }} className="flex items-center gap-2.5 text-white/85 text-sm bg-navy-900/50 border border-navy-700 rounded-xl px-3 py-2.5" data-id="element-296" data-mp="25rgzfwg5w8i1" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" data-id="element-297" data-mp="3ay8j54xz9hd7" />
                  {item}
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          x: stage >= 3 ? 0 : 20
        }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-6" data-id="element-298" data-mp="6jqul29zt37i">
            <div className="flex items-center gap-3 mb-5" data-id="element-299" data-mp="2f111k2t40pr">
              <div className="w-10 h-10 rounded-xl bg-sky-400/15 text-sky-400 flex items-center justify-center" data-id="element-300" data-mp="2i7qgg5161i0b">
                <Unlock className="w-5 h-5" data-id="element-301" data-mp="2n7up58vu18fz" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-302" data-mp="32ggbwvkhtcmd">Decentralize</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-id="element-303" data-mp="2xq9lqrac1k20">
              {decentralize.map((item, idx) => <motion.div key={item} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: stage >= 4 ? 1 : 0,
              y: stage >= 4 ? 0 : 8
            }} transition={{
              delay: stage >= 4 ? idx * 0.06 : 0
            }} className="flex items-center gap-2.5 text-white/85 text-sm bg-navy-900/50 border border-navy-700 rounded-xl px-3 py-2.5" data-id="element-304" data-mp="2sq524se4o97k" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" data-id="element-305" data-mp="20zh6twz4xy1t" />
                  {item}
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl px-7 py-5" data-id="element-306" data-mp="biv9xik85dcm">
          <p className="text-white/90 text-lg leading-relaxed" data-id="element-307" data-mp="pajnzre1abw8">
            The goal is not to centralize local decisions. The goal is to{' '}
            <span className="text-teal-400 font-semibold" data-id="element-308" data-mp="u8l4c4k2rx40">
              centralize the complexity
            </span>{' '}
            so local teams can move faster.
          </p>
        </motion.div>
      </div>
    </div>;
}